// Buscador de articulos para las pantallas de carga de pedido (la de
// administracion y la del local).
//
// La lista de precios larga se recorre a dedo: para cargar "alfajor de maicena"
// hay que acordarse en que categoria cae, abrirla y bajar hasta encontrarlo.
// Aca se escribe el nombre, se elige de la lista y la pantalla se encarga de
// abrir la categoria, marcar la fila y dejar el cursor en la cantidad.
//
// El modulo NO sabe como esta dibujada la grilla: solo avisa que articulo se
// eligio (alElegir) y cada pantalla hace lo suyo con marcarFila().

import { iconoBuscar } from "./iconos";

export interface ItemBuscable {
  id: number;
  descripcion: string;
  codigo: string;
  categoria_id: number;
  categoria: string;
  /** Texto chico a la derecha del resultado: precio, "sin precio", "3 cargados"... */
  detalle?: string;
}

/** Sin tildes y en minuscula: "Maicena" y "maicená" tienen que encontrarse igual. */
function normalizar(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

const TOPE_RESULTADOS = 12;

/**
 * Filtra por TODAS las palabras escritas (en cualquier orden): "maicena choco"
 * encuentra "Alfajor de chocolate y maicena". Tambien mira el codigo y el
 * nombre de la categoria, asi "tortas" trae la categoria entera.
 *
 * El orden importa mas que el filtro: el que escribe tres letras quiere el
 * articulo comun arriba, no el de nombre mas largo que tambien las contiene.
 * Por eso primero el nombre que ARRANCA con lo escrito, despues el que la tiene
 * como palabra suelta, y a igual peso gana el nombre mas corto ("Alfajor de
 * maicena" antes que "Alfajor de chocolate y maicena").
 */
export function buscar(items: ItemBuscable[], texto: string): ItemBuscable[] {
  const palabras = normalizar(texto).split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return [];
  const primera = palabras[0];
  const encontrados: Array<{ item: ItemBuscable; peso: number }> = [];
  for (const item of items) {
    const nombre = normalizar(item.descripcion);
    const heno = `${nombre} ${normalizar(item.codigo)} ${normalizar(item.categoria)}`;
    if (!palabras.every((p) => heno.includes(p))) continue;
    const peso = nombre.startsWith(primera)
      ? 0
      : nombre.split(/[^a-z0-9]+/).some((w) => w.startsWith(primera))
        ? 1
        : 2;
    encontrados.push({ item, peso });
  }
  return encontrados
    .sort(
      (a, b) =>
        a.peso - b.peso ||
        a.item.descripcion.length - b.item.descripcion.length ||
        a.item.descripcion.localeCompare(b.item.descripcion),
    )
    .slice(0, TOPE_RESULTADOS)
    .map((e) => e.item);
}

export interface OpcionesBuscador {
  placeholder?: string;
  /** Se llama en cada tecla: siempre devuelve el catalogo del momento. */
  items: () => ItemBuscable[];
  alElegir: (item: ItemBuscable) => void;
}

/** Dibuja el buscador dentro de `caja` y lo deja funcionando. */
export function montarBuscador(caja: HTMLElement, opciones: OpcionesBuscador): void {
  caja.innerHTML = `
    <div class="relative">
      <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-masa-400">${iconoBuscar}</span>
      <input data-buscar type="search" autocomplete="off" class="campo pl-10"
             placeholder="${esc(opciones.placeholder ?? "Buscar un artículo por nombre o código...")}" />
      <div data-resultados role="listbox"
           class="absolute inset-x-0 top-full z-30 mt-1 hidden max-h-72 overflow-y-auto rounded-xl border border-crema-200 bg-papel shadow-lg"></div>
    </div>`;

  const inp = caja.querySelector("[data-buscar]") as HTMLInputElement;
  const panel = caja.querySelector("[data-resultados]") as HTMLElement;
  let resultados: ItemBuscable[] = [];
  let activo = 0;

  function cerrar(): void {
    panel.classList.add("hidden");
    panel.innerHTML = "";
    resultados = [];
    activo = 0;
  }

  function pintarResultados(): void {
    if (resultados.length === 0) {
      panel.innerHTML = `<div class="px-3 py-3 text-sm text-masa-400">No hay artículos con ese nombre.</div>`;
      panel.classList.remove("hidden");
      return;
    }
    panel.innerHTML = resultados
      .map(
        (it, i) => `
        <button type="button" data-i="${i}" role="option" aria-selected="${i === activo}"
          class="flex w-full items-center gap-2 border-b border-crema-100 px-3 py-2 text-left text-sm last:border-b-0 ${
            i === activo ? "bg-crema-100" : "hover:bg-crema-50"
          }">
          <span class="min-w-0 flex-1">
            <span class="block truncate font-medium">${esc(it.descripcion)}</span>
            <span class="block truncate text-xs text-masa-400">${esc(it.categoria)}${
              it.codigo ? ` · #${esc(it.codigo)}` : ""
            }</span>
          </span>
          ${it.detalle ? `<span class="shrink-0 text-xs tabular-nums text-masa-500">${esc(it.detalle)}</span>` : ""}
        </button>`,
      )
      .join("");
    panel.classList.remove("hidden");
    panel.querySelector<HTMLElement>(`[data-i="${activo}"]`)?.scrollIntoView({ block: "nearest" });
  }

  function elegir(i: number): void {
    const item = resultados[i];
    if (!item) return;
    inp.value = "";
    cerrar();
    opciones.alElegir(item);
  }

  inp.addEventListener("input", () => {
    const texto = inp.value.trim();
    if (texto.length === 0) {
      cerrar();
      return;
    }
    resultados = buscar(opciones.items(), texto);
    activo = 0;
    pintarResultados();
  });

  inp.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") {
      cerrar();
      return;
    }
    if (resultados.length === 0) return;
    if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
      ev.preventDefault();
      activo = (activo + (ev.key === "ArrowDown" ? 1 : resultados.length - 1)) % resultados.length;
      pintarResultados();
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      elegir(activo);
    }
  });

  // mousedown y no click: el click llega despues del blur, que ya cerro el panel.
  panel.addEventListener("mousedown", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-i]");
    if (!btn) return;
    ev.preventDefault();
    elegir(Number(btn.dataset.i));
  });

  // Tocar fuera cierra. El timeout deja pasar el mousedown de un resultado.
  inp.addEventListener("blur", () => setTimeout(cerrar, 150));
}

/**
 * Abre la categoria, deja la fila marcada y el cursor en la cantidad.
 *
 * Las filas se ubican por `data-fila="<producto_id>"` y la categoria por
 * `details[data-cat="<categoria_id>"]`: las dos pantallas dibujan distinto
 * (tabla y lista) pero respetan esos atributos.
 */
export function marcarFila(grilla: HTMLElement, productoId: number, categoriaId: number): void {
  const det = grilla.querySelector<HTMLDetailsElement>(`details[data-cat="${categoriaId}"]`);
  if (det && !det.open) det.open = true; // el listener de toggle lo recuerda
  // Todo seguido, sin esperar un frame: abrir el <details> ya deja la fila en el
  // documento, y scrollIntoView calcula la posicion en el momento. Con
  // requestAnimationFrame la marca no llegaba a aparecer si la pestana no
  // estaba a la vista (el navegador no le da frames a lo que no se ve).
  const fila = grilla.querySelector<HTMLElement>(`[data-fila="${productoId}"]`);
  if (!fila) return;
  grilla.querySelectorAll(".resaltado").forEach((e) => e.classList.remove("resaltado"));
  fila.classList.add("resaltado");
  fila.scrollIntoView({ behavior: "smooth", block: "center" });
  const cant = fila.querySelector<HTMLInputElement>("input.cant");
  if (cant) {
    cant.focus({ preventScroll: true });
    cant.select();
  }
}
